import { defaultPrivileges } from '@/helpers/privileges';
import * as types from '@/store/types';

const state = {
    rolesModulesRestrictions: {
        'account_manager': { settings: { write: false } },
    },
    currentUserModulesRestrictions: {},
};

const getters = {
    currentUserModulesRestrictions: state => state.currentUserModulesRestrictions,

    isFraudProtectionAvailable: (state, getters) => getters?.currentUserHasModuleAccess?.('fraud_protection.general', 'read')
        || [ 'advertisers', 'offers', 'tracking_links' ].some(module => getters?.currentUserHasModuleAccess?.(`fraud_protection.${module}`, 'read')),
};

const actions = {
    populateCurrentUserModulesRestrictions ({ state, commit, rootState }) {
        const currentUser = rootState.currentUser,
            currentUserHasReadOnlyRoles = currentUser?.roles.every(role => role.endsWith('read_only')),
            currentModulesRestrictions = {
                // custom restrictions based on user roles
                ...Object.keys(state.rolesModulesRestrictions).reduce((acc, role) => ({
                    ...acc,
                    ...currentUser?.roles.includes(role) ? state.rolesModulesRestrictions[role] : {},
                }), {}),

                // disable write privilege in every module if user has only "read only" role(s)
                ...(currentUserHasReadOnlyRoles
                    ? Object.keys(defaultPrivileges).reduce((acc, module) => ({ ...acc, [module]: { write: false } }), {})
                    : {}
                ),

                // custom restrictions
                users: {
                    list: !!currentUser?.privileges?.users?.list,
                    create: !!currentUser?.privileges?.users?.create,
                    read: !!currentUser?.privileges?.users?.show,
                    update: !!currentUser?.privileges?.users?.update,
                    write: !!currentUser?.privileges?.users?.update, // TODO: use write or update, not both
                    delete: !!currentUser?.privileges?.users?.delete,
                },
                settings: {
                    read: currentUser?.roles?.some(role => ['admin', 'admin_read_only', 'settings_manager', 'settings_manager_read_only'].includes(role)),
                    write: !!currentUser?.privileges?.config?.update, // TODO: use write or update, not both
                },
                tasks: {
                    events_bulk_rejection: !!currentUser?.privileges?.system_settings?.tasks?.csv?.['csv_event_status_change'],
                },
                publishers: {
                    read: currentUser?.roles?.some(role => !['billing_manager', 'billing_manager_read_only'].includes(role)),
                    write: currentUser?.roles?.some(role => !['billing_manager', 'billing_manager_read_only'].includes(role) && !role.endsWith('read_only')),  // TODO: use write or update, not both
                },
                smart_links: {
                    read: currentUser?.roles?.some(role => ![
                        'billing_manager',
                        'billing_manager_read_only',
                        'settings_manager',
                        'settings_manager_read_only',
                        'user_manager',
                        'user_manager_read_only',
                    ].includes(role)),
                    write: currentUser?.roles?.some(role => ![
                        'billing_manager',
                        'billing_manager_read_only',
                        'settings_manager',
                        'settings_manager_read_only',
                    ].includes(role) && !role.endsWith('read_only')),
                },
                traffic_filtering: {
                    read: !!currentUser?.privileges?.traffic_filters?.list,
                    write: !currentUserHasReadOnlyRoles && !!currentUser?.privileges?.traffic_filters?.update, // TODO: use write or update, not both
                },
                applications: {
                    list: !!currentUser?.privileges?.applications?.applications_list,
                    read: !!currentUser?.privileges?.applications?.applications_show,
                    create: !!currentUser?.privileges?.applications?.applications_create,
                    update: !!currentUser?.privileges?.applications?.applications_update, // TODO: use write or update, not both
                    write: !!currentUser?.privileges?.applications?.applications_update,

                    events_list: !!currentUser?.privileges?.applications?.applications_events_list,
                    events_create: !!currentUser?.privileges?.applications?.applications_events_create,
                    events_update: !!currentUser?.privileges?.applications?.applications_events_show && !!currentUser?.privileges?.applications?.applications_events_update,
                    events_delete: !!currentUser?.privileges?.applications?.applications_events_delete,

                    campaigns_used_list: !!currentUser?.privileges?.applications?.applications_campaigns_used_list,
                },
                external_integrations: {
                    read: currentUser?.roles?.some(role => [
                        'account_manager',
                        'account_manager_read_only',
                        'admin',
                        'admin_read_only',
                        'advertiser_manager',
                        'advertiser_manager_read_only',
                    ].includes(role)),
                    write: currentUser?.roles?.some(role => [
                        'account_manager',
                        'admin',
                        'advertiser_manager',
                    ].includes(role)), // TODO: use write or update, not both
                },
                schedules: {
                    read: !!currentUser?.privileges?.schedules?.list,
                    write: currentUser?.roles?.some(role => [
                        'account_manager',
                        'admin',
                    ].includes(role)), // TODO: use write or update, not both
                },
                analytics: {
                    adjust: !!currentUser?.privileges?.analytics?.adjust,
                },
                optimizations: {
                    offers: {
                        update: !!currentUser?.privileges?.offers?.optimizations?.update,
                    },
                    partners: {
                        update: !!currentUser?.privileges?.partners?.optimizations?.update,
                    },
                },
                fraud_protection: {
                    general: {
                        read: !!currentUser?.privileges?.fraud_protection?.general?.show,
                        write: !!currentUser?.privileges?.fraud_protection?.general?.update,
                    },
                    advertisers: {
                        read: !!currentUser?.privileges?.fraud_protection?.advertisers?.list && !!currentUser?.privileges?.fraud_protection?.advertisers?.show,
                        update: !!currentUser?.privileges?.fraud_protection?.advertisers?.show && !!currentUser?.privileges?.fraud_protection?.advertisers?.update,
                        create: !!currentUser?.privileges?.fraud_protection?.advertisers?.default && !!currentUser?.privileges?.fraud_protection?.advertisers?.create,
                    },
                    offers: {
                        read: !!currentUser?.privileges?.fraud_protection?.offers?.list && !!currentUser?.privileges?.fraud_protection?.offers?.show,
                        update: !!currentUser?.privileges?.fraud_protection?.offers?.show && !!currentUser?.privileges?.fraud_protection?.offers?.update,
                        create: !!currentUser?.privileges?.fraud_protection?.offers?.default && !!currentUser?.privileges?.fraud_protection?.offers?.create,
                    },
                    tracking_links: {
                        read: !!currentUser?.privileges?.fraud_protection?.tracking_links?.list && !!currentUser?.privileges?.fraud_protection?.tracking_links?.show,
                        update: !!currentUser?.privileges?.fraud_protection?.tracking_links?.show && !!currentUser?.privileges?.fraud_protection?.tracking_links?.update,
                        create: !!currentUser?.privileges?.fraud_protection?.tracking_links?.default && !!currentUser?.privileges?.fraud_protection?.tracking_links?.create,
                    },
                },
                traffic_control: {
                    read: currentUser?.roles?.some(role => ['admin', 'admin_read_only'].includes(role)),
                    write: currentUser?.roles?.some(role => ['admin'].includes(role)),
                },
                offers: {
                    read: !!currentUser?.privileges?.offers?.show,
                    write: !!currentUser?.privileges?.offers?.update,
                },
                kpi_control: {
                    read: !!currentUser?.privileges?.analytics_kpis?.show,
                    write: !!currentUser?.privileges?.analytics_kpis?.update,
                },
                foreign_leads: {
                    read: !!currentUser?.privileges?.foreign_leads?.campaigns?.list,
                    write: !!currentUser?.privileges?.foreign_leads?.campaigns?.sync,
                },
            };

        commit(types.POPULATE_CURRENT_USER_MODULES_RESTRICTIONS, currentModulesRestrictions);

        return currentModulesRestrictions;
    },
};

const mutations = {
    [types.POPULATE_CURRENT_USER_MODULES_RESTRICTIONS] (state, payload) {
        state.currentUserModulesRestrictions = payload;
    },
};

export default {
    getters,
    actions,
    mutations,
    state,
};
